import { inject, Injectable, signal } from '@angular/core';
import { UserService } from '../user-management/user.service';
import { MessageService } from 'primeng/api';
import { RestApiV2Service } from '../http/rest-api-v2.service';
import { Treatment } from '../../../shared/models/treatments/treatment.model';
import { take } from 'rxjs';
import { DispenserUserInfo, InsurerUserInfo, IpsUserInfo, PharmaceuticalUserInfo } from '../../../shared/models/users/user.model';
import { IpsData } from './ips.service';
import { PharmaceuticalData } from './pharmaceutical.service';
import { TreatmentUtils } from '../../../features/treatments-module/treatmentUtils';
import { DispenserData } from './dispenser.service';
import { Effect } from '../../../shared/models/treatments/effects.model';
import { Pacient } from '../../../shared/models/users/pacient.model';
import { DashboardInteraction, DashboardInteractionItem, InteractionType } from '../../../shared/models/treatments/dashboardInteraction.model';
import { HighDemandMolecule } from '../../../shared/models/treatments/high-demand-molecule.model';
import { Latency } from '../../../shared/models/treatments/latency.model';

export interface IpsDataWithUserInfo extends IpsUserInfo, IpsData {}

export interface PharmaceuticalDataWithUserInfo extends PharmaceuticalUserInfo, PharmaceuticalData {}

export interface InsurerData {
  treatments: Treatment[];
}

export interface DispenserDataWithUserInfo {
  dispenser: DispenserUserInfo;
  treatments: Treatment[];
}

export interface InsurerDashboardStats {
  treatmentsByMedicineGroup: { group: string; count: number }[];
  alertsByMedicineGroup: { group: string; count: number }[];
  pacientAdherence: {
    adherent: number;
    mediumAdherent: number;
    nonAdherent: number;
  };
  formulasByLaboratory: { laboratory: string; count: number }[];
}

@Injectable({
  providedIn: 'root',
})
export class InsurerService {
  private readonly restApiV2 = inject(RestApiV2Service);
  private readonly messageService = inject(MessageService);
  private readonly userService = inject(UserService);

  data = signal<InsurerData>({ treatments: [] });
  insurerIps = signal<IpsDataWithUserInfo[]>([]);
  insurerPharmaceuticals = signal<PharmaceuticalDataWithUserInfo[]>([]);
  insurerDispensers = signal<DispenserDataWithUserInfo[]>([]);

  // Flags para controlar si ya se cargaron los datos
  ipsLoaded = false;
  pharmaceuticalsLoaded = false;
  dispensersLoaded = false;

  getMyData() {
    const insurerInfo = this.userService.userData()?.user_info as InsurerUserInfo;
    const response = this.restApiV2.get<{data: { data: InsurerData } }>('insurer/get-my-data', { insurer_id: insurerInfo.eps_id });

    response.request$
    .pipe(take(1))
    .subscribe({
      next: response => {
        this.processData(response.data.data);
      },
      error: error => {
        if(!error) return;

        this.messageService.add({ severity: 'error', detail: 'Error al obtener los datos' });
      }
    });
  }

  getIps() {
    if(this.ipsLoaded) return;

    const insurerInfo = this.userService.userData()?.user_info as InsurerUserInfo;
    const response = this.restApiV2.get<{data: { ips: IpsDataWithUserInfo[] } }>('insurer/get-ips', { insurer_id: insurerInfo.eps_id });

    response.request$
    .pipe(take(1))
    .subscribe({
      next: response => {
        this.insurerIps.set(response.data.ips);
        this.ipsLoaded = true;
      },
      error: error => {
        if(!error) return;
        this.messageService.add({ severity: 'error', detail: 'Error al obtener los datos de los prestadores' });
      }
    });
  }

  getPharmaceuticals() {
    if(this.pharmaceuticalsLoaded) return;

    const insurerInfo = this.userService.userData()?.user_info as InsurerUserInfo;
    const response = this.restApiV2.get<{data: { pharmaceuticals: PharmaceuticalDataWithUserInfo[] } }>('insurer/get-pharmaceuticals', { insurer_id: insurerInfo.eps_id });

    response.request$
    .pipe(take(1))
    .subscribe({
      next: response => {
        this.insurerPharmaceuticals.set(response.data.pharmaceuticals);
        this.pharmaceuticalsLoaded = true;
      },
      error: error => {
        if(!error) return;
        this.messageService.add({ severity: 'error', detail: 'Error al obtener los datos de los laboratorios' });
      }
    });
  }

  getDispensers() {
    if(this.dispensersLoaded) return;

    const insurerInfo = this.userService.userData()?.user_info as InsurerUserInfo;
    const response = this.restApiV2.get<{data: { dispensers: DispenserDataWithUserInfo[]} }>('insurer/get-dispensers', { insurer_id: insurerInfo.eps_id });
    response.request$
    .pipe(take(1))
    .subscribe({
      next: response => {
        this.insurerDispensers.set(response.data.dispensers);
        this.dispensersLoaded = true;
      },
      error: error => {
        if(!error) return;
        this.messageService.add({ severity: 'error', detail: 'Error al obtener los datos de los dispensadores' });
      }
    });
  }

  processData(data: InsurerData) {
    console.log(data);
    this.data.set(data);

    this.getIps();
    this.getPharmaceuticals();
    this.getDispensers();
  }

  /**
   * Procesa las estadísticas del dashboard del insurer
   */
  processInsurerDashboardStats(data: InsurerData): InsurerDashboardStats {
    if (!data.treatments || data.treatments.length === 0) {
      return {
        treatmentsByMedicineGroup: [],
        alertsByMedicineGroup: [],
        pacientAdherence: {
          adherent: 0,
          mediumAdherent: 0,
          nonAdherent: 0,
        },
        formulasByLaboratory: [],
      };
    }

    // Tratamientos por grupo de medicamentos
    const treatmentsByGroupMap = new Map<string, number>();
    data.treatments.forEach(treatment => {
      if (treatment.medicine?.group) {
        const group = treatment.medicine.group;
        treatmentsByGroupMap.set(group, (treatmentsByGroupMap.get(group) || 0) + 1);
      }
    });
    const treatmentsByMedicineGroup = Array.from(treatmentsByGroupMap.entries())
      .map(([group, count]) => ({ group, count }))
      .sort((a, b) => b.count - a.count);

    // Efectos adversos reportados por grupo de medicamentos
    const alertsByGroupMap = new Map<string, number>();
    data.treatments.forEach(treatment => {
      if (treatment.medicine?.group && treatment.interactions) {
        const group = treatment.medicine.group;
        const alertCount = treatment.interactions.length;
        alertsByGroupMap.set(group, (alertsByGroupMap.get(group) || 0) + alertCount);
      }
    });
    const alertsByMedicineGroup = Array.from(alertsByGroupMap.entries())
      .map(([group, count]) => ({ group, count }))
      .sort((a, b) => b.count - a.count);

    // Estados de adherencia del paciente
    let adherent = 0;
    let mediumAdherent = 0;
    let nonAdherent = 0;

    data.treatments.forEach(treatment => {
      const adherenceStatus = TreatmentUtils.getAdherenceStatus(treatment);
      if (adherenceStatus === 'Adherente') {
        adherent++;
      } else if (adherenceStatus === 'Adherente medio') {
        mediumAdherent++;
      } else if (adherenceStatus === 'No adherente') {
        nonAdherent++;
      }
    });

    // Fórmulas por laboratorio
    const formulasByLaboratoryMap = new Map<string, number>();
    data.treatments.forEach(treatment => {
      if (treatment.medicine?.marketed_by?.laboratory) {
        const laboratory = treatment.medicine.marketed_by.laboratory;
        formulasByLaboratoryMap.set(laboratory, (formulasByLaboratoryMap.get(laboratory) || 0) + 1);
      }
    });
    const formulasByLaboratory = Array.from(formulasByLaboratoryMap.entries())
      .map(([laboratory, count]) => ({ laboratory, count }))
      .sort((a, b) => b.count - a.count);

    return {
      treatmentsByMedicineGroup,
      alertsByMedicineGroup,
      pacientAdherence: {
        adherent,
        mediumAdherent,
        nonAdherent,
      },
      formulasByLaboratory,
    };
  }

  manageInsurerTreatment(treatment: Treatment, socketType: 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT') {
    this.data.update(currentData => {
      const treatmentIndex = currentData.treatments.findIndex(t => t.id === treatment.id);
      
      if (treatmentIndex !== -1) {
        return {
          ...currentData,
          treatments: currentData.treatments.map((t, idx) => idx === treatmentIndex ? treatment : t)
        };
      }
      
      if (socketType === 'NEW_TREATMENT') {
        return {
          ...currentData,
          treatments: [...currentData.treatments, treatment]
        };
      }
      
      return currentData;
    });
  }

  manageInsurerIpsTreatment(treatment: Treatment, socketType: 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT') {
    this.insurerIps.update(currentIps => 
      currentIps.map(ips => this.updateTreatmentInDataStructure(ips, treatment, socketType, ips.professionals))
    );
  }

  manageInsurerPharmaceuticalTreatment(treatment: Treatment, socketType: 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT') {
    this.insurerPharmaceuticals.update(currentPharmaceuticals => 
      currentPharmaceuticals.map(pharmaceutical => 
        this.updateTreatmentInDataStructure(pharmaceutical, treatment, socketType, pharmaceutical.professionals)
      )
    );
  }

  private updateTreatmentInDataStructure<T extends { pacients: {pacient: any, treatments: Treatment[] }[], professionals: any[] }>(
    data: T,
    treatment: Treatment,
    socketType: 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT',
    professionals: {professional: any, pacients: {pacient: any, treatments: Treatment[] }[]}[]
  ): T {
    const pacientIndex = data.pacients.findIndex(p => p.pacient.id === treatment.pacient_id);
    
    if (socketType === 'ALTER_TREATMENT' || socketType === 'REMOVE_TREATMENT') {
      if (pacientIndex === -1) {
        return { ...data, professionals: this.updateTreatmentInProfessionals(professionals, treatment) };
      }
      
      const pacient = data.pacients[pacientIndex];
      const treatmentIndex = pacient.treatments.findIndex(t => t.id === treatment.id);
      
      if (treatmentIndex === -1) {
        return { ...data, professionals: this.updateTreatmentInProfessionals(professionals, treatment) };
      }
      
      return {
        ...data,
        pacients: data.pacients.map((p, idx) => 
          idx === pacientIndex 
            ? { ...p, treatments: p.treatments.map((t, tIdx) => tIdx === treatmentIndex ? treatment : t) }
            : p
        ),
        professionals: this.updateTreatmentInProfessionals(professionals, treatment)
      };
    }
    
    // NEW_TREATMENT
    if (pacientIndex !== -1) {
      const pacient = data.pacients[pacientIndex];
      const treatmentIndex = pacient.treatments.findIndex(t => t.id === treatment.id);
      
      return {
        ...data,
        pacients: data.pacients.map((p, idx) => 
          idx === pacientIndex
            ? { ...p, treatments: treatmentIndex !== -1 
                ? p.treatments.map((t, tIdx) => tIdx === treatmentIndex ? treatment : t)
                : [...p.treatments, treatment]
              }
            : p
        ),
        professionals: this.updateTreatmentInProfessionals(professionals, treatment)
      };
    }
    
    if (treatment.pacient) {
      return {
        ...data,
        pacients: [...data.pacients, { pacient: treatment.pacient, treatments: [treatment] }],
        professionals: this.updateTreatmentInProfessionals(professionals, treatment)
      };
    }
    
    return {
      ...data,
      professionals: this.updateTreatmentInProfessionals(professionals, treatment)
    };
  }

  private updateTreatmentInProfessionals(
    professionals: {professional: any, pacients: {pacient: any, treatments: Treatment[] }[]}[],
    treatment: Treatment
  ): {professional: any, pacients: {pacient: any, treatments: Treatment[] }[]}[] {
    return professionals.map(prof => {
      const pacientIndex = prof.pacients.findIndex(p => p.pacient.id === treatment.pacient_id);
      
      if (pacientIndex === -1) {
        return treatment.pacient
          ? { ...prof, pacients: [...prof.pacients, { pacient: treatment.pacient, treatments: [treatment] }] }
          : prof;
      }
      
      const pacient = prof.pacients[pacientIndex];
      const treatmentIndex = pacient.treatments.findIndex(t => t.id === treatment.id);
      
      return {
        ...prof,
        pacients: prof.pacients.map((p, idx) => 
          idx === pacientIndex
            ? { ...p, treatments: treatmentIndex !== -1 
                ? p.treatments.map((t, tIdx) => tIdx === treatmentIndex ? treatment : t)
                : [...p.treatments, treatment]
              }
            : p
        )
      };
    });
  }

  addEffectsToTreatments(effects: Effect[]) {
    this.data.update(currentData => ({
      ...currentData,
      treatments: currentData.treatments.map(treatment => {
        const treatmentEffects = effects.filter(e => e.treatment_id === treatment.id);
        if (treatmentEffects.length === 0) return treatment;
        
        const existingEffectIds = new Set(treatment.effects.map(e => e.id));
        const newEffects = treatmentEffects.filter(e => !existingEffectIds.has(e.id));
        
        return {
          ...treatment,
          effects: [...treatment.effects, ...newEffects]
        };
      })
    }));
  }

  addEffectsToInsurerIpsTreatments(effects: Effect[]) {
    this.insurerIps.update(currentIps => 
      currentIps.map(ips => ({
        ...ips,
        pacients: this.addEffectsToPacients(ips.pacients, effects),
        professionals: ips.professionals.map(prof => ({
          ...prof,
          pacients: this.addEffectsToPacients(prof.pacients, effects)
        }))
      }))
    );
  }

  addEffectsToInsurerPharmaceuticalTreatments(effects: Effect[]) {
    this.insurerPharmaceuticals.update(currentPharmaceuticals => 
      currentPharmaceuticals.map(pharmaceutical => ({
        ...pharmaceutical,
        pacients: this.addEffectsToPacients(pharmaceutical.pacients, effects),
        professionals: pharmaceutical.professionals.map(prof => ({
          ...prof,
          pacients: this.addEffectsToPacients(prof.pacients, effects)
        }))
      }))
    );
  }

  private addEffectsToPacients(
    pacients: {pacient: Pacient, treatments: Treatment[] }[],
    effects: Effect[]
  ): {pacient: Pacient, treatments: Treatment[] }[] {
    return pacients.map(pacient => ({
      ...pacient,
      treatments: pacient.treatments.map(treatment => {
        const treatmentEffects = effects.filter(e => e.treatment_id === treatment.id);
        if (treatmentEffects.length === 0) return treatment;
        
        const existingEffectIds = new Set(treatment.effects.map(e => e.id));
        const newEffects = treatmentEffects.filter(e => !existingEffectIds.has(e.id));
        
        return {
          ...treatment,
          effects: [...treatment.effects, ...newEffects]
        };
      })
    }));
  }

  updateDashboardInteractionInIps(interaction: DashboardInteractionItem) {
    this.insurerIps.update(currentIps => 
      currentIps.map(ips => ({
        ...ips,
        professionals: ips.professionals.map(prof => 
          prof.professional.id === interaction.professional_id
            ? {
                ...prof,
                professional: {
                  ...prof.professional,
                  interactions: this.addOrUpdateInteraction(prof.professional.interactions, interaction)
                }
              }
            : prof
        )
      }))
    );
  }

  updateDashboardInteractionInPharmaceuticals(interaction: DashboardInteractionItem) {
    this.insurerPharmaceuticals.update(currentPharmaceuticals => 
      currentPharmaceuticals.map(pharmaceutical => ({
        ...pharmaceutical,
        professionals: pharmaceutical.professionals.map(prof => 
          prof.professional.id === interaction.professional_id
            ? {
                ...prof,
                professional: {
                  ...prof.professional,
                  interactions: this.addOrUpdateInteraction(prof.professional.interactions, interaction)
                }
              }
            : prof
        )
      }))
    );
  }

  private addOrUpdateInteraction(interactions: DashboardInteraction, item: DashboardInteractionItem): DashboardInteraction {
    const arrayKey = this.getInteractionArrayKey(item.interaction_type);
    if (!arrayKey) return interactions;

    const currentArray = interactions[arrayKey] || [];
    const existingIndex = currentArray.findIndex(i => i.id === item.id);
    
    const updatedArray = existingIndex !== -1
      ? currentArray.map((i, idx) => idx === existingIndex ? item : i)
      : [...currentArray, item];

    return {
      ...interactions,
      [arrayKey]: updatedArray
    };
  }

  private getInteractionArrayKey(type: InteractionType): keyof DashboardInteraction | null {
    switch (type) {
      case InteractionType.READ_ALERTS: return 'read_alerts';
      case InteractionType.UNREAD_ALERTS: return 'unread_alerts';
      case InteractionType.READ_MESSAGES: return 'read_messages';
      case InteractionType.UNREAD_MESSAGES: return 'unread_messages';
      case InteractionType.READ_PRESCRIPTIONS: return 'read_prescriptions';
      case InteractionType.UNREAD_PRESCRIPTIONS: return 'unread_prescriptions';
      default: return null;
    }
  }

  addOrUpdateMoleculeInIps(molecule: HighDemandMolecule) {
    this.insurerIps.update(currentIps => 
      currentIps.map(ips => {
        const moleculeIndex = ips.molecules.findIndex(m => m.id === molecule.id);
        
        if (moleculeIndex !== -1) {
          return {
            ...ips,
            molecules: ips.molecules.map((m, idx) => idx === moleculeIndex ? molecule : m)
          };
        }
        
        return {
          ...ips,
          molecules: [...ips.molecules, molecule]
        };
      })
    );
  }

  addOrUpdateMoleculeInPharmaceuticals(molecule: HighDemandMolecule) {
    this.insurerPharmaceuticals.update(currentPharmaceuticals => 
      currentPharmaceuticals.map(pharmaceutical => {
        const moleculeIndex = pharmaceutical.molecules.findIndex(m => m.id === molecule.id);
        
        if (moleculeIndex !== -1) {
          return {
            ...pharmaceutical,
            molecules: pharmaceutical.molecules.map((m, idx) => idx === moleculeIndex ? molecule : m)
          };
        }
        
        return {
          ...pharmaceutical,
          molecules: [...pharmaceutical.molecules, molecule]
        };
      })
    );
  }

  addOrUpdateLatencyInPharmaceuticals(latency: Latency) {
    this.insurerPharmaceuticals.update(currentPharmaceuticals => 
      currentPharmaceuticals.map(pharmaceutical => ({
        ...pharmaceutical,
        professionals: pharmaceutical.professionals.map(prof => 
          prof.professional.id === latency.professional_id
            ? {
                ...prof,
                professional: {
                  ...prof.professional,
                  latency: this.addOrUpdateLatencyInArray(prof.professional.latency, latency)
                }
              }
            : prof
        )
      }))
    );
  }

  private addOrUpdateLatencyInArray(latencyArray: Latency[], latency: Latency): Latency[] {
    const latencyIndex = latencyArray.findIndex(l => l.id === latency.id);
    
    if (latencyIndex !== -1) {
      return latencyArray.map((l, idx) => idx === latencyIndex ? latency : l);
    }
    
    return [...latencyArray, latency];
  }
}
